import {
  Location,
  ProfileSizes,
  UserImpactAggregates,
  ProjectType,
  User,
  Wordpress,
  GetImpactReportCsvRequestBody,
  ImpactType,
  values,
} from "@ecologi/shared";
import React, { useState } from "react";
import { DeprecatedProfileCard } from "../DeprecatedProfileCard";
import {
  AddNewItemProps,
  ProfileChecklistItemHandlers,
} from "../ProfileChecklist";
import { ProfileHeader } from "../ProfileHeader";
import { Wrapper } from "../Wrapper";
import styles from "./Profile.module.scss";
import { ImpactStats, CombinedImpactQuantities } from "../ImpactStats";
import styled from "@emotion/styled";
import { CreateFundReportModal, CreateReportButton } from "../FundReport";

const HeaderContainer = styled.div`
  width: 100%;
  margin: 60px auto;
  @media ${({ theme }) => theme.bp.mobileUp} {
    margin-top: 104px;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
`;

type Props = {
  createDonationCharge: (payload: any) => Promise<void>;
  isCarbonOnly: boolean;
  isEmbed: boolean;
  isSuccess: boolean;
  location: Location;
  profile: User;
  projects: ProjectType[];
  redirectToCheckout: (payload: any) => Promise<void>;
  resumeSubscription: () => Promise<void>;
  user: User | null;
} & ProfileChecklistItemHandlers &
  AddNewItemProps;

const Loading = () => <div className={styles.profileLoading} />;

type ProfileHeaderSectionProps = {
  allProjects: Wordpress.Project[];
  children?: React.ReactNode;
  isOwnProfile: boolean;
  isLoading: boolean;
  isFundReportEnabled: boolean;
  impactTotals: Record<ImpactType, CombinedImpactQuantities>;
  onResumeSubscription: () => Promise<any>;
  profile: User;
  profileTitle: { title: string; intro?: string };
  size?: ProfileSizes;
  userImpactAggregates: UserImpactAggregates;
  getImpactReportCsv: (args: GetImpactReportCsvRequestBody) => Promise<string>;
  accessToken: string;
};

const LegacyCards = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  id?: string;
}) => (
  <section className={styles.cards} {...props}>
    {children}
  </section>
);

export function ProfileHeaderSection({
  allProjects,
  children,
  isOwnProfile,
  isFundReportEnabled,
  isLoading,
  impactTotals,
  onResumeSubscription,
  profile,
  profileTitle,
  size = ProfileSizes.DEFAULT,
  userImpactAggregates,
  getImpactReportCsv,
  accessToken,
}: ProfileHeaderSectionProps) {
  const [isCreateFundReportModalOpen, setIsCreateFundReportModalOpen] =
    useState(false);

  return (
    <Wrapper>
      <ProfileHeader
        isOwnProfile={isOwnProfile}
        profileTitle={profileTitle}
        resumeSubscription={onResumeSubscription}
        size={size}
        user={profile}
      />
      {!children ? (
        <HeaderContainer>
          <ImpactStats
            allProjects={allProjects}
            isOwnProfile={isOwnProfile}
            impactTotals={impactTotals}
            isLoading={isLoading}
            user={profile}
            userImpactAggregates={userImpactAggregates}
          />
          {isFundReportEnabled && isOwnProfile && (
            <CreateReportButton
              opaque
              onClick={() => setIsCreateFundReportModalOpen(true)}
            />
          )}
        </HeaderContainer>
      ) : (
        children
      )}
      {isCreateFundReportModalOpen && (
        <CreateFundReportModal
          getImpactReportCsv={getImpactReportCsv}
          accessToken={accessToken}
          user={profile}
          isOpen={isCreateFundReportModalOpen}
          onClose={() => setIsCreateFundReportModalOpen(false)}
          hasPendingImpact={values(impactTotals).some(
            (total) => total.pending > 0
          )}
        />
      )}
    </Wrapper>
  );
}

export const ProfilePage = (props: Props) => {
  return null;
};

ProfilePage.Loading = Loading;
ProfileHeaderSection.DeprecatedCards = LegacyCards;
ProfileHeaderSection.DeprecatedCard = DeprecatedProfileCard;
